// Trust Payments Hosted Form Apple Pay Integration
import { FetchError } from "ofetch";
import {
  MerchantValidationError,
  PaymentAuthorizationError,
  PaymentCancellationError,
} from "~/service/apple-pay/errors";

// Trust Payments Configuration - for hosted forms
const TRUST_PAYMENTS_CONFIG = {
  sitereference: "swipelux113558",
  password: "!?,*$wCx!9HM", // Used for site security hash only
  hostedFormUrl: "https://payments.securetrading.net/process/payments/details",
};

// Hardcoded payment parameters - no need for orderToken validation
const HARDCODED_PAYMENT_PARAMS = {
  amount: 25.00,
  currency: "GBP",
  orderReference: "CHECKOUT-HARDCODED-001",
  customerEmail: "checkout@swipelux.com",
  customerName: "Checkout User",
  countryCode: "GB",
  merchantLabel: "Swipelux Checkout",
};

/**
 * Generate HMAC-SHA256 hash for Trust Payments site security
 */
function generateSiteSecurityHash(
  sitereference: string,
  password: string,
  amount: string,
  currency: string,
  timestamp: string
): Promise<string> {
  return new Promise(async (resolve, reject) => {
    try {
      const data = sitereference + password + amount + currency + timestamp;
      
      // Use Web Crypto API for HMAC-SHA256
      const encoder = new TextEncoder();
      const key = await crypto.subtle.importKey(
        "raw",
        encoder.encode(password),
        { name: "HMAC", hash: "SHA-256" },
        false,
        ["sign"]
      );
      
      const signature = await crypto.subtle.sign("HMAC", key, encoder.encode(data));
      const hashArray = Array.from(new Uint8Array(signature));
      const hashBase64 = btoa(String.fromCharCode.apply(null, hashArray));
      
      resolve(hashBase64);
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Get current timestamp in Trust Payments format
 */
function getCurrentTimestamp(): string {
  const now = new Date();
  return now.getFullYear() + '-' +
    String(now.getMonth() + 1).padStart(2, '0') + '-' +
    String(now.getDate()).padStart(2, '0') + ' ' +
    String(now.getHours()).padStart(2, '0') + ':' +
    String(now.getMinutes()).padStart(2, '0') + ':' +
    String(now.getSeconds()).padStart(2, '0');
}

/**
 * Create Trust Payments hosted form with Apple Pay enabled
 */
async function createTrustPaymentsForm(): Promise<{ 
  formData: Record<string, string | string[]>; 
  html: string; 
}> {
  const timestamp = getCurrentTimestamp();
  const amountFormatted = HARDCODED_PAYMENT_PARAMS.amount.toFixed(2);
  
  // Generate site security hash
  const siteSecurity = await generateSiteSecurityHash(
    TRUST_PAYMENTS_CONFIG.sitereference,
    TRUST_PAYMENTS_CONFIG.password,
    amountFormatted,
    HARDCODED_PAYMENT_PARAMS.currency,
    timestamp
  );
  
  // Create form data with Apple Pay enabled
  const formData: Record<string, string | string[]> = {
    sitereference: TRUST_PAYMENTS_CONFIG.sitereference,
    currencyiso3a: HARDCODED_PAYMENT_PARAMS.currency,
    mainamount: amountFormatted,
    orderreference: HARDCODED_PAYMENT_PARAMS.orderReference,
    version: "2",
    stprofile: "default",
    sitesecurity: siteSecurity,
    sitesecuritytimestamp: timestamp,
    
    // Apple Pay / Wallet Configuration
    wallettype: ["APPLEPAY", "GOOGLEPAY"],
    paymentmethods: ["CARD", "WALLET"],
    enableapplepayweb: "1",
    enablegooglepay: "1",
    
    // Customer information
    billingemail: HARDCODED_PAYMENT_PARAMS.customerEmail,
    billingfirstname: HARDCODED_PAYMENT_PARAMS.customerName,
    billinglastname: "User",
    
    // Required fields
    strequiredfields: ["billingfirstname", "billinglastname", "billingemail"],
  };
  
  // Generate HTML form
  const formFields = Object.entries(formData)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return value.map(v => 
          `<input type="hidden" name="${key}" value="${v}">`
        ).join('\n');
      }
      return `<input type="hidden" name="${key}" value="${value}">`;
    })
    .join('\n');
  
  const html = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Trust Payments - Apple Pay Enabled</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            margin: 0;
            padding: 20px;
            background: #f5f5f5;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
        }
        .container {
            background: white;
            padding: 40px;
            border-radius: 12px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
            text-align: center;
            max-width: 400px;
            width: 100%;
        }
        .header {
            margin-bottom: 30px;
        }
        .payment-info {
            background: #f8f9fa;
            padding: 20px;
            border-radius: 8px;
            margin-bottom: 30px;
        }
        .amount {
            font-size: 32px;
            font-weight: bold;
            color: #333;
            margin-bottom: 10px;
        }
        .order-ref {
            font-size: 14px;
            color: #666;
            margin: 5px 0;
        }
        .submit-btn {
            background: linear-gradient(135deg, #007AFF, #5856D6);
            color: white;
            border: none;
            padding: 16px 32px;
            border-radius: 12px;
            font-size: 18px;
            font-weight: 600;
            cursor: pointer;
            width: 100%;
            transition: all 0.3s ease;
        }
        .submit-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 25px rgba(0,122,255,0.3);
        }
        .apple-pay-notice {
            background: #e8f5e8;
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 20px;
            font-size: 14px;
            color: #2d5d2d;
        }
        .loading {
            display: inline-block;
            width: 20px;
            height: 20px;
            border: 2px solid #f3f3f3;
            border-top: 2px solid #007AFF;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-right: 10px;
        }
        @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
        }
        .debug-info {
            background: #f0f8ff;
            padding: 15px;
            border-radius: 8px;
            margin-top: 20px;
            font-size: 12px;
            color: #333;
            text-align: left;
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <h1>🍎 Trust Payments</h1>
            <p>Secure Payment Processing</p>
        </div>
        
        <div class="payment-info">
            <div class="amount">${HARDCODED_PAYMENT_PARAMS.currency} ${amountFormatted}</div>
            <div class="order-ref">Order: ${HARDCODED_PAYMENT_PARAMS.orderReference}</div>
            <div class="order-ref">Email: ${HARDCODED_PAYMENT_PARAMS.customerEmail}</div>
        </div>
        
        <div class="apple-pay-notice">
            🍎 <strong>Apple Pay Enabled!</strong><br>
            You'll see Apple Pay as an option on the next page<br>
            (Safari browser required)
        </div>
        
        <form id="payment-form" method="POST" action="${TRUST_PAYMENTS_CONFIG.hostedFormUrl}">
            ${formFields}
            <button type="submit" class="submit-btn" onclick="showLoading(this)">
                Continue to Payment
            </button>
        </form>
        
        <div class="debug-info">
            <strong>🔧 Configuration:</strong><br>
            • Site Reference: ${TRUST_PAYMENTS_CONFIG.sitereference}<br>
            • Apple Pay: Enabled<br>
            • Google Pay: Enabled<br>
            • Hosted Form: Trust Payments Secure Server<br>
            • Timestamp: ${timestamp}
        </div>
    </div>
    
    <script>
        function showLoading(button) {
            button.innerHTML = '<div class="loading"></div>Loading secure payment page...';
            button.disabled = true;
        }
        
        // Auto-submit after 2 seconds for demo (remove for production)
        setTimeout(function() {
            console.log('Auto-submitting to Trust Payments hosted form with Apple Pay enabled...');
            document.getElementById('payment-form').submit();
        }, 2000);
        
        console.log('Trust Payments form ready with Apple Pay enabled');
        console.log('Form data:', ${JSON.stringify(formData, null, 2)});
    </script>
</body>
</html>`;
  
  return { formData, html };
}

/**
 * Check if we're in a browser environment
 * Note: Apple Pay availability will be handled by Trust Payments hosted form
 */
export function isApplePayAvailable(): boolean {
  return typeof window !== "undefined";
}

/**
 * Initiate Trust Payments Apple Pay Session using hosted form
 */
export function initiateApplePaySession(): Promise<any> {
  return new Promise(async (resolve, reject) => {
    try {
      console.log("🍎 Creating Trust Payments hosted form with Apple Pay...");
      
      // Create form with Apple Pay enabled
      const { formData, html } = await createTrustPaymentsForm();
      
      console.log("✅ Form created with Apple Pay enabled:", formData);
      
      // Create and submit form programmatically
      const formElement = document.createElement('div');
      formElement.innerHTML = html;
      
      // Replace current page content or open in new window
      if (confirm('Redirect to Trust Payments secure payment page with Apple Pay?')) {
        // Replace current page
        document.open();
        document.write(html);
        document.close();
        
        resolve({ success: true, method: 'hosted-form' });
      } else {
        reject(new PaymentCancellationError());
      }
      
    } catch (error) {
      console.error("❌ Error creating Trust Payments form:", error);
      reject(
        new MerchantValidationError(
          error instanceof Error ? error.message : "Failed to create payment form"
        )
      );
    }
  });
}

/**
 * Legacy function signatures to maintain compatibility
 * Now ignores the parameters and uses hardcoded values
 */
export function initiateApplePaySession_legacy({
  merchantKey,
  orderToken,
  fromAmount,
  toAmount,
}: {
  merchantKey?: string;
  orderToken?: string;
  fromAmount?: any;
  toAmount?: any;
}) {
  // Ignore all parameters and use hardcoded Trust Payments hosted form
  console.log("🔄 Using Trust Payments hosted form with Apple Pay (parameters ignored)");
  return initiateApplePaySession();
}